import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import BreadCrumb from "components/common/BreadCrumb";
import { BackButton } from "components/common/Buttons";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { ClientInfoCard } from "components/common/ClientInfoCard";
import { getDocumentTitle } from "utils/helpers";
import { getInvoice, getInvoiceOptions } from "features/Invoices/services/InvoiceService";
import {
  InvoiceOption,
  InvoiceSingleValue,
} from "features/Invoices/components/InvoiceSelectComponents";
import { getClientAdvance, getClientAdvanceOptions } from "features/ClientAdvances/services/ClientAdvanceService";
import {
  ClientAdvanceOption,
  ClientAdvanceSingleValue,
} from "features/ClientAdvances/components/ClientAdvanceSelectComponents";
import {
  ClientAdvanceSourceType,
  ClientAdvanceStatus,
} from "features/ClientAdvances/constants/client-advance.constants";
import type { Client } from "features/Clients/types/client.types";
import type { AmcContract } from "features/Amc/types/amc.types";
import { formatCurrency } from "utils/currency";
import schema from "./schema";
import {
  createPaymentExpectation,
  getPaymentExpectation,
  updatePaymentExpectation,
} from "../services/PaymentExpectationService";
import {
  PaymentExpectationDirection,
  PaymentExpectationSourceType,
} from "../constants/payment-expectation.constants";
import type { PaymentExpectation } from "../types/payment-expectation.types";

interface Props {
  isEdit?: boolean;
}

const SOURCE_TYPE_OPTIONS = [
  { value: PaymentExpectationSourceType.INVOICE, label: "Invoice" },
  { value: PaymentExpectationSourceType.ADVANCE, label: "Advance" },
];

function apiErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}

interface ResolvedClient {
  id: number;
  client: Client;
}

const PaymentExpectationForm = ({ isEdit = false }: Props) => {
  document.title = getDocumentTitle(
    isEdit ? "Edit collection" : "New collection",
  );
  const navigate = useNavigate();
  const { id } = useParams();
  const [record, setRecord] = useState<PaymentExpectation | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [resolvedClient, setResolvedClient] = useState<ResolvedClient | null>(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState<number | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    const numId = Number(id);
    if (!numId) return;
    getPaymentExpectation(numId)
      .then((res) => {
        if (res.success) setRecord(res.data);
        else setLoadError("Could not load record.");
      })
      .catch(() => setLoadError("Could not load record."));
  }, [isEdit, id]);

  // Pre-populate client from the existing record on edit
  useEffect(() => {
    if (record?.client && record?.clientId) {
      setResolvedClient({ id: record.clientId, client: record.client as Client });
    }
  }, [record]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      sourceType: record?.sourceType ?? PaymentExpectationSourceType.INVOICE,
      sourceId: record?.sourceId ?? null,
      expectedAmount:
        record?.expectedAmount != null ? String(record.expectedAmount) : "",
      expectedDate: record?.expectedDate ?? "",
      promisedBy: record?.promisedBy ?? "",
      followUpDate: record?.followUpDate ?? "",
      remarks: record?.remarks ?? "",
    },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          sourceType: values.sourceType as PaymentExpectationSourceType,
          sourceId: values.sourceId ?? undefined,
          expectedAmount: Number(values.expectedAmount),
          expectedDate: values.expectedDate,
          promisedBy: values.promisedBy || undefined,
          followUpDate: values.followUpDate || undefined,
          remarks: values.remarks || undefined,
          direction: PaymentExpectationDirection.IN,
        };

        if (isEdit && id) {
          const res = await updatePaymentExpectation(Number(id), payload);
          if (res.success) {
            toast.success(res.message ?? "Expectation updated");
            navigate(-1);
          }
        } else {
          const res = await createPaymentExpectation(payload);
          if (res.success) {
            toast.success(res.message ?? "Expectation created");
            navigate(-1);
          }
        }
      } catch (err) {
        toast.error(apiErrorMessage(err, "Could not save expectation"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue } = validation;
  const sourceType = validation.values.sourceType as PaymentExpectationSourceType;
  const sourceId = validation.values.sourceId;
  const isInvoice = sourceType === PaymentExpectationSourceType.INVOICE;
  const isAmountLocked =
    isEdit && record != null && Number(record.receivedAmount) > 0;
  const isAmountFromAdvance = !isEdit && advanceAmount != null;

  // Resolve client whenever sourceId changes (create mode only; edit uses record)
  useEffect(() => {
    if (isEdit) return;
    if (!sourceId) {
      setResolvedClient(null);
      setAdvanceAmount(null);
      if (!isInvoice) {
        void setFieldValue("expectedAmount", "");
      }
      return;
    }
    let cancelled = false;
    setClientLoading(true);
    const numId = Number(sourceId);

    if (isInvoice) {
      getInvoice(numId)
        .then((res) => {
          if (cancelled) return;
          const inv = res.data;
          if (inv?.client && inv?.clientId) {
            setResolvedClient({ id: inv.clientId, client: inv.client as Client });
          } else {
            setResolvedClient(null);
          }
        })
        .catch(() => { if (!cancelled) setResolvedClient(null); })
        .finally(() => { if (!cancelled) setClientLoading(false); });
    } else {
      getClientAdvance(numId)
        .then((res) => {
          if (cancelled) return;
          const advance = res.data;
          const amc = advance?.source as AmcContract | null | undefined;
          if (amc?.client && amc?.clientId) {
            setResolvedClient({ id: amc.clientId, client: amc.client as Client });
          } else {
            setResolvedClient(null);
          }
          if (advance?.amount != null) {
            setAdvanceAmount(advance.amount);
            void setFieldValue("expectedAmount", String(advance.amount));
          } else {
            setAdvanceAmount(null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setResolvedClient(null);
            setAdvanceAmount(null);
          }
        })
        .finally(() => { if (!cancelled) setClientLoading(false); });
    }
    return () => { cancelled = true; };
  }, [sourceId, isInvoice, isEdit, setFieldValue]);

  const sourceLabel = isInvoice ? "Invoice" : "Advance";
  const sourceFetchFn = isInvoice ? getInvoiceOptions : getClientAdvanceOptions;
  const sourceComponents = isInvoice
    ? { Option: InvoiceOption, SingleValue: InvoiceSingleValue }
    : { Option: ClientAdvanceOption, SingleValue: ClientAdvanceSingleValue };
  const advanceRequestParams = isInvoice
    ? undefined
    : {
        status: ClientAdvanceStatus.DRAFT,
        withCancelledDisbursement: true,
        sourceType: ClientAdvanceSourceType.AMC,
      };

  const sourceDefaultOption =
    record?.sourceId && record.source
      ? [{ value: record.sourceId, label: record.source.code }]
      : record?.sourceId
        ? [{ value: record.sourceId, label: `#${record.sourceId}` }]
        : undefined;

  if (loadError) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">{loadError}</Alert>
        </Container>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "Collections", path: "/finance/payment-expectations" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? "Edit Collection" : "New Collection"}
          pageTitle="Finance"
          items={breadcrumbItems}
        />
        <Form onSubmit={validation.handleSubmit}>
          <Row className="g-4">
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Collection details</h5>
                </CardHeader>
                <CardBody className="vstack gap-3">

                  {/* Source type */}
                  <div>
                    <Label className="form-label fw-medium">
                      Source type <span className="text-danger">*</span>
                    </Label>
                    <div className="d-flex gap-3">
                      {SOURCE_TYPE_OPTIONS.map((o) => (
                        <div className="form-check" key={o.value}>
                          <Input
                            type="radio"
                            id={`sourceType-${o.value}`}
                            name="sourceType"
                            className="form-check-input"
                            value={o.value}
                            checked={sourceType === o.value}
                            disabled={isEdit}
                            onChange={() => {
                              void setFieldValue("sourceType", o.value);
                              void setFieldValue("sourceId", null);
                              void setFieldValue("expectedAmount", "");
                              setResolvedClient(null);
                              setAdvanceAmount(null);
                            }}
                          />
                          <Label
                            check
                            className="form-check-label"
                            for={`sourceType-${o.value}`}
                          >
                            {o.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {isEdit ? (
                      <small className="text-muted">
                        Source type cannot be changed after creation.
                      </small>
                    ) : null}
                  </div>

                  {/* Source record */}
                  <div>
                    <Label className="form-label fw-medium">{sourceLabel}</Label>
                    <AsyncPaginatedSelect
                      key={sourceType}
                      defaultOption={sourceDefaultOption}
                      fetchOptions={sourceFetchFn}
                      placeholder={`Search ${sourceLabel.toLowerCase()}s…`}
                      value={
                        validation.values.sourceId != null
                          ? [validation.values.sourceId]
                          : []
                      }
                      onChange={(val: any) => {
                        const selected = Array.isArray(val) ? val[0] : val;
                        void validation.setFieldValue(
                          "sourceId",
                          selected?.value ?? null,
                        );
                      }}
                      isDisabled={isEdit}
                      components={sourceComponents}
                      requestParams={advanceRequestParams}
                    />
                    {!isInvoice ? (
                      <small className="text-muted">
                        Only approved AMC advances are listed here.
                      </small>
                    ) : null}
                    {isEdit ? (
                      <small className="text-muted">
                        {sourceLabel} cannot be changed after creation.
                      </small>
                    ) : null}
                  </div>

                  {/* Amount */}
                  <div>
                    <Label className="form-label fw-medium">
                      Expected amount <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="expectedAmount"
                      min={0.01}
                      step={0.01}
                      value={validation.values.expectedAmount}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        validation.touched.expectedAmount &&
                        !!validation.errors.expectedAmount
                      }
                      disabled={isAmountLocked || isAmountFromAdvance}
                      placeholder="0.00"
                    />
                    {validation.touched.expectedAmount &&
                    validation.errors.expectedAmount ? (
                      <FormFeedback>
                        {validation.errors.expectedAmount as string}
                      </FormFeedback>
                    ) : null}
                    {isAmountLocked ? (
                      <small className="text-muted">
                        Amount cannot be changed once a receipt has been recorded
                        (received:{" "}
                        {formatCurrency(Number(record?.receivedAmount))}).
                      </small>
                    ) : null}
                    {isAmountFromAdvance ? (
                      <small className="text-muted">
                        Amount is pre-filled from the selected advance and cannot be changed.
                      </small>
                    ) : null}
                  </div>

                  {/* Expected Date */}
                  <div>
                    <Label className="form-label fw-medium">
                      Expected date <span className="text-danger">*</span>
                    </Label>
                    <Flatpickr
                      className={`form-control${
                        validation.touched.expectedDate &&
                        validation.errors.expectedDate
                          ? " is-invalid"
                          : ""
                      }`}
                      value={
                        validation.values.expectedDate
                          ? moment(validation.values.expectedDate).toDate()
                          : undefined
                      }
                      options={{ dateFormat: "d/m/Y" }}
                      onChange={([date]) => {
                        void validation.setFieldValue(
                          "expectedDate",
                          date ? moment(date).format("YYYY-MM-DD") : "",
                        );
                      }}
                      placeholder="dd/mm/yyyy"
                    />
                    {validation.touched.expectedDate &&
                    validation.errors.expectedDate ? (
                      <FormFeedback className="d-block">
                        {validation.errors.expectedDate as string}
                      </FormFeedback>
                    ) : null}
                  </div>

                  {/* Promised By */}
                  <div>
                    <Label className="form-label fw-medium">Promised by</Label>
                    <Input
                      type="text"
                      name="promisedBy"
                      value={validation.values.promisedBy}
                      onChange={validation.handleChange}
                      placeholder="e.g. Ravi Kumar"
                    />
                  </div>

                  {/* Follow-up date */}
                  <div>
                    <Label className="form-label fw-medium">Follow-up date</Label>
                    <Flatpickr
                      className={`form-control${
                        validation.touched.followUpDate &&
                        validation.errors.followUpDate
                          ? " is-invalid"
                          : ""
                      }`}
                      value={
                        validation.values.followUpDate
                          ? moment(validation.values.followUpDate).toDate()
                          : undefined
                      }
                      options={{ dateFormat: "d/m/Y" }}
                      onChange={([date]) => {
                        void validation.setFieldValue(
                          "followUpDate",
                          date ? moment(date).format("YYYY-MM-DD") : "",
                        );
                      }}
                      placeholder="dd/mm/yyyy (optional)"
                    />
                    {validation.touched.followUpDate &&
                    validation.errors.followUpDate ? (
                      <FormFeedback className="d-block">
                        {validation.errors.followUpDate as string}
                      </FormFeedback>
                    ) : null}
                  </div>

                  {/* Remarks */}
                  <div>
                    <Label className="form-label fw-medium">Remarks</Label>
                    <Input
                      type="textarea"
                      name="remarks"
                      rows={3}
                      value={validation.values.remarks}
                      onChange={validation.handleChange}
                      placeholder="e.g. Client confirmed via call"
                    />
                  </div>
                </CardBody>
                <CardFooter>
                  <div className="d-flex justify-content-end gap-2">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden
                          />
                          Saving…
                        </>
                      ) : isEdit ? (
                        "Save changes"
                      ) : (
                        "Create collection"
                      )}
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Client info — resolved from selected source */}
              {clientLoading ? (
                <Card>
                  <CardBody className="text-center py-3">
                    <Spinner size="sm" className="text-muted" />
                  </CardBody>
                </Card>
              ) : resolvedClient ? (
                <ClientInfoCard
                  client={resolvedClient.client}
                  clientId={resolvedClient.id}
                />
              ) : null}

              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Guide</h5>
                </CardHeader>
                <CardBody>
                  <ul className="list-unstyled vstack gap-2 text-muted small mb-0">
                    <li>
                      <i className="ri-information-line me-1 text-primary" />
                      Status is computed automatically — never set directly.
                    </li>
                    <li>
                      <i className="ri-information-line me-1 text-primary" />
                      Once any receipt is recorded, the expected amount is locked.
                    </li>
                    <li>
                      <i className="ri-information-line me-1 text-warning" />
                      For Invoice source, the backend validates that the amount
                      does not exceed the invoice outstanding balance.
                    </li>
                    <li>
                      <i className="ri-information-line me-1 text-primary" />
                      Follow-up date is an internal reminder — it does not affect
                      status.
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default PaymentExpectationForm;
